import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Shield, Loader2, CheckCircle, AlertTriangle, Lock, User, Mail, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({ name: "", password: "", confirmPassword: "" });

  useEffect(() => {
    validateInvite();
  }, [token]);

  const validateInvite = async () => {
    try {
      const res = await axios.get(`${API}/auth/invite/${token}`);
      setInvite(res.data);
      setForm((f) => ({ ...f, name: res.data.name || "" }));
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired invitation link");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Please enter your full name"); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (form.password !== form.confirmPassword) { toast.error("Passwords do not match"); return; }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/accept-invite`, {
        token,
        name: form.name.trim(),
        password: form.password,
      }, { withCredentials: true });

      // Cookie is set by backend; store user profile for UI
      const { user } = res.data;
      localStorage.setItem("contrack_user", JSON.stringify(user));
      toast.success("Account created! Welcome to ConTrack.");
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create account");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 size={32} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-sm p-8 shadow-sm">
            <AlertTriangle size={48} className="mx-auto text-amber-500 mb-4" />
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Invitation Invalid</h1>
            <p className="text-slate-500 text-sm mb-6">{error}</p>
            <Button onClick={() => navigate("/login")} className="rounded-sm">Go to Login</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4" data-testid="accept-invite-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Con<span className="text-cyan-500">T</span>rack
          </h1>
          <p className="text-slate-500 text-sm mt-1">Contract Lifecycle Management</p>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-sm p-6 shadow-sm">
          {/* Invite info */}
          <div className="mb-6 p-4 bg-sky-50 dark:bg-sky-950/20 border border-sky-200 dark:border-sky-800 rounded-sm">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={16} className="text-sky-600" />
              <span className="text-sm font-medium text-sky-800 dark:text-sky-400">You've been invited!</span>
            </div>
            <span className="text-xs text-sky-700 dark:text-sky-500">
              <strong>{invite.invited_by_name}</strong> invited you to join as{" "}
              <Badge className="bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300 rounded-sm text-[10px] ml-1">
                {invite.role?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
              </Badge>
            </span>
          </div>

          <form onSubmit={handleAccept} className="space-y-4">
            {/* Email (read-only) */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5"><Mail size={12} /> Email</Label>
              <Input value={invite.email} disabled className="rounded-sm bg-slate-50 dark:bg-slate-800" data-testid="invite-email" />
            </div>

            {/* Name */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5"><User size={12} /> Full Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Enter your full name"
                className="rounded-sm"
                data-testid="invite-name"
                autoFocus
              />
            </div>

            {/* Department (read-only if set) */}
            {invite.department && (
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5"><Building2 size={12} /> Department</Label>
                <Input value={invite.department} disabled className="rounded-sm bg-slate-50 dark:bg-slate-800" />
              </div>
            )}

            {/* Password */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5"><Lock size={12} /> Password *</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Min 8 chars, upper + lower + number + special"
                className="rounded-sm"
                data-testid="invite-password"
              />
            </div>

            {/* Confirm Password */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5"><Lock size={12} /> Confirm Password *</Label>
              <Input
                type="password"
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                placeholder="Re-enter your password"
                className="rounded-sm"
                data-testid="invite-confirm-password"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting || !form.name.trim() || !form.password || !form.confirmPassword}
              className="w-full rounded-sm gap-2"
              data-testid="accept-invite-btn"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />}
              Create My Account
            </Button>
          </form>

          <p className="text-center text-xs text-slate-400 mt-4">
            Already have an account?{" "}
            <a href="/login" className="text-sky-600 hover:underline">Log in</a>
          </p>
        </div>
      </div>
    </div>
  );
}
